<template>
  <AppDialogContent
    v-model="showModal"
    :error="formError"
    :persistent="formSubmited"
    :loading="formSubmited || $apollo.queries.personRoles.loading"
    data-test="eventStaffForm"
    @submit-form="handleSubmit()"
  >
    <template #header>{{
      $t(
        "common:common.addstaff.label",
        "Add staff member",
        "Label of the add staff section"
      )
    }}</template>

    <v-form :disabled="formSubmited">
      <AppSelectRole
        v-model="form.role"
        :error-messages="validationErrors('role')"
        :have-scopes="['RoleInEvent']"
        :discipline-codenames="[event.discipline.codename]"
        return-object
        :label="$t('events:eventRolesManagement.addStaff.role.label')"
        data-test="eventStaffRole"
        @input="v$.form.role.$touch()"
        @blur="v$.form.role.$touch()"
      />
      <AppSelectPeople
        :key="showModal"
        v-model="form.personId"
        data-test="eventStaffPerson"
        :label="$t('events:eventRolesManagement.addStaff.person.label')"
        :hint="hintForPersonSelection"
        :single-select="true"
        :error-messages="validationErrors('personId')"
        :additional-query-params="{
          applicableForRoleIds: [form.role ? form.role.id : null].filter(
            Boolean
          ),
          applicableForRoleIdsCheckDate: event.startDate
        }"
        ignore-mixin
        outlined
        persistent-hint
        required
        @input="v$.form.personId.$touch()"
        @blur="v$.form.personId.$touch()"
      />

      <AppShowPersonRolesOverlapping
        v-if="personRoles.length > 0"
        :person-roles="personRoles"
      />
    </v-form>
  </AppDialogContent>
</template>
<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";
import { get } from "lodash-es";
import saveMeta from "@/mixins/save_meta";
import appFormMixin from "@/mixins/appFormMixin";
import { useProfQualificationHelper } from "@composables/masterdata/person/profQualifications";
import { PersonRoleFragment } from "./personRoleMethods";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelectPeople from "@elements/AppSelects/AppSelectPeople.vue";
import AppSelectRole from "@AppSelects/AppSelectRole.vue";
import AppShowPersonRolesOverlapping from "@AppShows/AppShowPersonRolesOverlapping.vue";

export default defineComponent({
  name: "EventPersonRoleDialog",
  components: {
    AppSelectPeople,
    AppSelectRole,
    AppDialogContent,
    AppShowPersonRolesOverlapping
  },
  mixins: [appFormMixin, saveMeta],
  props: {
    event: {
      type: Object,
      required: true
    },
    mutateOptions: {
      type: Object,
      default: () => ({})
    }
  },
  setup() {
    const appForm = appFormMixin.setup();
    const masterProfQualifications = useProfQualificationHelper();

    const personRoles = ref([]);

    return {
      get,
      ...appForm,
      ...masterProfQualifications,
      personRoles
    };
  },
  data() {
    return {
      showModalVal: false,
      form: {
        role: null,
        personId: null
      },
      formError: null,
      formSubmited: false,
      appFormMixinValidationsConfVariable: "initForm.vuelidateValidations",
      appFormMixinReadOnlyFieldsVariable: "initForm.readOnlyFields",
      initForm: {
        vuelidateValidations: {
          role: { required: true },
          personId: { required: true }
        },
        readOnlyFields: []
      }
    };
  },
  computed: {
    showModal: {
      get() {
        return this.showModalVal;
      },
      set(v) {
        this.showModalVal = v;
      }
    },
    hintForPersonSelection() {
      const profQualificationIds = get(
        this.form,
        "role.profQualificationIds",
        []
      );

      if (profQualificationIds.length) {
        return (
          this.$t("events:eventRolesManagement.addStaff.role.explainer") +
          " " +
          this.masterProfQualifications
            .filter(i => profQualificationIds.includes(i.id))
            .map(i => i.name)
            .join(", ")
        );
      }
      return this.$t("events:eventRolesManagement.addStaff.person.hint");
    }
  },
  apollo: {
    personRoles: {
      query: gql`
        query fetchOfficialOverlappingPersonRoles($personId: ID) {
          person(id: $personId) {
            id
            personRoles(haveScopes: ["RoleInEvent"]) {
              id
              ${AppShowPersonRolesOverlapping.fragments.personRoleFields}
              event {
                id
                startDate
              }
            }
          }
        }
      `,
      variables() {
        return {
          personId: this.form.personId
        };
      },
      skip() {
        return !this.showModalVal || !this.form.personId;
      },
      result({ loading, data }) {
        if (loading) return;
        const eventStartDate = this.$dayjs(this.event.startDate);
        const eventEndDate = this.$dayjs(this.event.endDate);
        this.personRoles =
          data.person?.personRoles?.filter(pr => {
            const prStart = this.$dayjs(pr.event.startDate);
            const prEnd = this.$dayjs(pr.event.endDate);
            return prEnd >= eventStartDate && prStart <= eventEndDate;
          }) ?? [];
      },
      manual: true,
      fetchPolicy: "network-only"
    }
  },
  watch: {
    "form.personId"() {
      this.personRoles = [];
    },
    async showModalVal(newVal) {
      if (newVal === false) {
        if ((await this.saveMetaUnsaveConfirm()) === true) {
          this.showModal = false;
          this.form = {};
          this.saveMetaSetOriginalForm(this.form);
        } else {
          this.showModal = true;
        }
      }
    }
  },
  methods: {
    openForm(item) {
      this.form = Object.assign(
        { personId: null, roleId: null, role: null },
        item
      );
      this.saveMetaSetOriginalForm(this.form);
      this.v$.form.$reset();
      this.formError = {};
      this.showModal = true;
    },
    validationErrors(fieldName) {
      return this.appFormMixinValidationErrorsForField(fieldName);
    },
    async handleSubmit() {
      this.formError = {};
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        return false;
      }

      this.formSubmited = true;

      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation personGrantRoleInEvent(
              $personId: ID!
              $roleId: ID!
              $eventId: ID!
            ) {
              personGrantRoleInEvent(
                input: {
                  personId: $personId
                  roleId: $roleId
                  eventId: $eventId
                }
              ) {
                event {
                  id
                  personRoles {
                    ${PersonRoleFragment}
                  }
                }
              }
            }
          `,
          ...this.mutateOptions,
          variables: {
            personId: this.form.personId,
            eventId: this.event.id,
            roleId: this.form.role.id
          }
        });
        this.$root.$snackbar("success");
        this.saveMetaSetOriginalForm(this.form);
        this.showModal = false;
      } catch (error) {
        this.$sentryThrowError.throwError(error, { level: "error" });
        this.formError = { type: "graphql", err: error };
      } finally {
        this.formSubmited = false;
      }
    }
  },
  fragments: {
    eventFields: gql`
      fragment _ on Event {
        startDate
        endDate
      }
    `,
    disciplineFields: gql`
      fragment _ on Discipline {
        id
        codename
      }
    `
  }
});
</script>
